import { showToast } from './toast';

function isDetachSubmitter(submitter: EventTarget | null): boolean {
    if (!(submitter instanceof HTMLElement)) {
        return false;
    }
    if (submitter.getAttribute('data-detach-submit') === 'true') {
        return true;
    }
    const name = submitter.getAttribute('name');
    return name != null && name.startsWith('submit_detach_');
}

function isNumericInt(s: string): boolean {
    const t = s.trim();
    if (t === '') {
        return false;
    }
    const n = Number(t);
    return Number.isInteger(n) && !Number.isNaN(n);
}

function syncCategorySelection(root: HTMLElement): void {
    const radio = root.querySelector<HTMLInputElement>('input[name="category"]:checked');
    const addCatTd = document.getElementById('add-cat-td');
    const select = document.getElementById('prior_old_list') as HTMLSelectElement | null;
    const oldBox = document.getElementById('old-prior-box');
    const newTypeRadio = document.getElementById('add-prior-type-new') as HTMLInputElement | null;
    const oldTypeRadio = document.getElementById('add-prior-type-old') as HTMLInputElement | null;

    if (!radio || !select || !oldBox) {
        return;
    }

    const label = radio.getAttribute('data-category-label') ?? '';
    if (addCatTd) {
        addCatTd.textContent = label;
    }

    const catId = radio.id.replace(/^cat_/, '');
    const tmpl = document.getElementById(`prior-opts-${catId}`) as HTMLTemplateElement | null;
    select.innerHTML = '';
    if (tmpl?.content) {
        select.appendChild(tmpl.content.cloneNode(true));
    }

    const hasOpts = select.options.length > 0;
    oldBox.classList.toggle('hidden', !hasOpts);
    if (!hasOpts && oldTypeRadio?.checked && newTypeRadio) {
        newTypeRadio.checked = true;
    }
}

function openEditForm(data: {
    id: string;
    name: string;
    level: string;
    creditFactor: string;
    showWarning: boolean;
}): void {
    const form = document.getElementById('admin-priority-edit-form');
    const idEl = document.getElementById('edit_priority_id') as HTMLInputElement | null;
    const nameEl = document.getElementById('edit_prior_name') as HTMLInputElement | null;
    const levelEl = document.getElementById('edit_prior_level') as HTMLInputElement | null;
    const credEl = document.getElementById('edit_prior_credfact') as HTMLInputElement | null;
    const warnEl = document.getElementById('edit_prior_warn') as HTMLInputElement | null;

    if (!form || !idEl || !nameEl || !levelEl || !credEl || !warnEl) {
        return;
    }

    idEl.value = data.id;
    nameEl.value = data.name;
    levelEl.value = data.level;
    credEl.value = data.creditFactor;
    warnEl.checked = data.showWarning;
    form.classList.remove('hidden');
    nameEl.focus();
}

function closeEditForm(): void {
    const form = document.getElementById('admin-priority-edit-form');
    if (form) {
        form.classList.add('hidden');
    }
}

function validateMainFormSubmit(e: SubmitEvent): void {
    const sub = e.submitter;
    if (isDetachSubmitter(sub)) {
        return;
    }

    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'admin-priority-main-form') {
        return;
    }

    const isAdd =
        (sub instanceof HTMLButtonElement && sub.name === 'add_prior') ||
        (sub instanceof HTMLInputElement && sub.name === 'add_prior');
    if (!isAdd) {
        return;
    }

    const newRadio = form.querySelector<HTMLInputElement>('#add-prior-type-new');
    const oldRadio = form.querySelector<HTMLInputElement>('#add-prior-type-old');
    const priorName = form.querySelector<HTMLInputElement>('#prior_name');
    const priorLevel = form.querySelector<HTMLInputElement>('#prior_level');
    const priorCred = form.querySelector<HTMLInputElement>('#prior_credfact');
    const priorOld = form.querySelector<HTMLSelectElement>('#prior_old_list');

    if (newRadio?.checked) {
        if (!priorName?.value.trim()) {
            e.preventDefault();
            showToast({ variant: 'error', message: 'Please fill in a priority name.' });
            return;
        }
        if (!priorLevel?.value.trim() || !isNumericInt(priorLevel.value)) {
            e.preventDefault();
            showToast({ variant: 'error', message: 'Priority level must be a whole number.' });
            return;
        }
        if (!priorCred?.value.trim() || !isNumericInt(priorCred.value)) {
            e.preventDefault();
            showToast({ variant: 'error', message: 'Credit factor must be a whole number.' });
            return;
        }
        return;
    }

    if (oldRadio?.checked && priorOld) {
        if (priorOld.selectedIndex < 0 || priorOld.value === '') {
            e.preventDefault();
            showToast({ variant: 'error', message: 'Choose an existing priority to attach.' });
        }
    }
}

function validateEditFormSubmit(e: SubmitEvent): void {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.id !== 'admin-priority-edit-form') {
        return;
    }

    const nameEl = form.querySelector<HTMLInputElement>('#edit_prior_name');
    const levelEl = form.querySelector<HTMLInputElement>('#edit_prior_level');
    const credEl = form.querySelector<HTMLInputElement>('#edit_prior_credfact');

    if (!nameEl?.value.trim()) {
        e.preventDefault();
        showToast({ variant: 'error', message: 'Please fill in a priority name.' });
        return;
    }
    if (!levelEl?.value.trim() || !isNumericInt(levelEl.value)) {
        e.preventDefault();
        showToast({ variant: 'error', message: 'Priority level must be a whole number.' });
        return;
    }
    if (!credEl?.value.trim() || !isNumericInt(credEl.value)) {
        e.preventDefault();
        showToast({ variant: 'error', message: 'Credit factor must be a whole number.' });
    }
}

export function initAdminPrioritiesPage(): void {
    const root = document.getElementById('admin-priorities-root');
    const mainForm = document.getElementById('admin-priority-main-form');
    if (!root || !mainForm) {
        return;
    }

    syncCategorySelection(root);

    root.addEventListener('change', (ev) => {
        const t = ev.target;
        if (t instanceof HTMLInputElement && t.name === 'category') {
            syncCategorySelection(root);
        }
        if (t instanceof HTMLSelectElement && t.id === 'prior_old_list') {
            const oldRadio = document.getElementById('add-prior-type-old') as HTMLInputElement | null;
            if (oldRadio) {
                oldRadio.checked = true;
            }
        }
    });

    root.addEventListener('click', (ev) => {
        const btn = (ev.target as HTMLElement).closest('[data-edit-priority]');
        if (!(btn instanceof HTMLButtonElement)) {
            return;
        }
        const id = btn.getAttribute('data-priority-id');
        const name = btn.getAttribute('data-priority-name');
        const level = btn.getAttribute('data-priority-level');
        const creditFactor = btn.getAttribute('data-priority-credit-factor');
        const sw = btn.getAttribute('data-priority-show-warning');
        if (id == null || name == null || level == null || creditFactor == null) {
            return;
        }
        openEditForm({
            id,
            name,
            level,
            creditFactor,
            showWarning: sw === 'true',
        });
    });

    document.getElementById('admin-priority-edit-cancel')?.addEventListener('click', () => {
        closeEditForm();
    });

    mainForm.addEventListener('submit', validateMainFormSubmit);

    const editForm = document.getElementById('admin-priority-edit-form');
    editForm?.addEventListener('submit', validateEditFormSubmit);
}
